import { buildDateValidation, commonValidators } from './commonValidators';
import { evaluateCondition } from './utils';

/**
 * @function buildRules - building rules for the question into an object that react-hook-form can use
 * @param {import("../types/question").QuestionProp} question - question to build rules for
 * @param {boolean} isShowing - whether the question is showing or not as to return true when validating if the question is not showing
 * @param {{ screenData?: Record<string, unknown>; formData?: Record<string, unknown> }} context - optional screenData/formData for conditional validation
 * @returns {Object} - object of rules for react-hook-form
 */

type Validate =
  | Partial<
      ReturnType<typeof buildDateValidation> & {
        validateType: typeof commonValidators;
      }
    >
  | ReturnType<typeof buildValidate>;

type BuildRulesContext = {
  screenData?: Record<string, unknown>;
  formData?: Record<string, unknown>;
};

export default function buildRules(
  question,
  isShowing,
  context: BuildRulesContext = {},
) {
  if (!isShowing) {
    return {
      validate: () => true,
    };
  }

  const { rules } = question;
  return makeRules(rules, context);
}

function makeRules(rules, context: BuildRulesContext = {}) {
  if (!rules) {
    return {};
  }

  let validate: Validate = {};
  if (rules.validationType) {
    validate.validateType = commonValidators[rules.validationType];
  }

  if ('dateRules' in rules) {
    validate = { ...validate, ...buildDateValidation(rules.dateRules) };
  }
  if (rules.validate) {
    validate = buildValidate(rules.validate, context);
  }
  const builtRules = {
    ...rules,
    validate,
  };
  return builtRules;
}

function buildValidate(validateObject, context: BuildRulesContext = {}) {
  if (validateObject === undefined) {
    return () => true;
  }

  // "unless" makes the validator true when the condition is met.
  // Example: { "gt": 0, "unless": { "when": "isCooperative", "dataSource": "screenData" } }
  const unlessCondition = validateObject.unless;
  const shouldSkip = unlessCondition
    ? evaluateCondition(
        unlessCondition,
        context.formData ?? {},
        context.screenData ?? {},
      )
    : false;
  if (shouldSkip) {
    return () => true;
  }

  if (Object.hasOwn(validateObject, 'is')) {
    return (value) => value === validateObject.is;
  }

  if (Object.hasOwn(validateObject, 'isNot')) {
    return (value) => value !== validateObject.isNot;
  }

  if (Object.hasOwn(validateObject, 'gte')) {
    return (value) => value >= validateObject.gte;
  }

  if (Object.hasOwn(validateObject, 'lte')) {
    const lteValue = validateObject.lte;
    const message = validateObject.message;
    return (value) => {
      const isValid = value <= lteValue;
      return (
        isValid || (message ?? `Must be less than or equal to ${lteValue}`)
      );
    };
  }

  if (Object.hasOwn(validateObject, 'gt')) {
    const gtValue = validateObject.gt;
    const message = validateObject.message;
    return (value) => {
      const isValid = value > gtValue;
      return isValid || (message ?? `Must be greater than ${gtValue}`);
    };
  }

  if (Object.hasOwn(validateObject, 'lt')) {
    return (value) => value < validateObject.lt;
  }

  if (Object.hasOwn(validateObject, 'onlySpaceNotAllowed')) {
    return (value) => value.trim().length > 0;
  }

  // checks if input matches the pattern passed in (e.g. ###-###-#####)
  if (Object.hasOwn(validateObject, 'formatPattern')) {
    const patternRegexString =
      '^' + validateObject.formatPattern.replaceAll('#', String.raw`\d`) + '$';
    const patternRegex = new RegExp(patternRegexString);
    const digitCount = (validateObject.formatPattern.match(/#/g) ?? []).length;
    const rawDigitsRegex = new RegExp(String.raw`^\d{${digitCount}}$`);
    return (value) => {
      // accounts for api data that has not yet been formatted
      if (typeof value === 'number') return true;

      const trimmedValue = (value ?? '').trim();

      if (rawDigitsRegex.test(trimmedValue)) return true;

      return (
        patternRegex.test(trimmedValue) ||
        `Must match format: ${validateObject.formatPattern}`
      );
    };
  }

  return () => true;
}
